/**
 * Location context — tracks the currently selected location for the session.
 *
 * - Scoped users (Bartender, BarManager, Brewer): location list comes from
 *   the /me response (session.locations).
 * - Full-access users (Leader, Admin, Payroll): all locations are fetched via
 *   GET /locations so they can switch to any site.
 *
 * The selected location ID is persisted to localStorage so the user's last
 * choice survives a page reload. On first visit (or if the stored ID is no
 *  longer in the list), the first location in the list is auto-selected.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { listLocations } from '../api/client';
import type { Location } from '../api/types';
import { useSession } from './session';

const STORAGE_KEY = 'canto-location-id';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LocationContextValue {
  /** The full Location object for the selected location, or null. */
  location: Location | null;
  /** The selected location ID, or null if none are available. */
  locationId: string | null;
  /** All locations accessible to this user. */
  locations: Location[];
  /** True while the full location list is being fetched (full-access users). */
  isLoadingLocations: boolean;
  /** Select a different location. */
  setLocationId: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const LocationContext = createContext<LocationContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export function useLocation(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation() called outside <LocationProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function LocationProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { locations: sessionLocations, isFullAccess } = useSession();

  // Full-access users (Leader/Admin/Payroll) fetch the complete list.
  const { data: allLocationsData, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => listLocations({ includeDeleted: false }),
    enabled: isFullAccess,
    staleTime: 5 * 60 * 1000, // 5 min — location names rarely change
  });

  const locations: Location[] = useMemo(
    () =>
      isFullAccess
        ? (allLocationsData?.items ?? [])
        : sessionLocations,
    [isFullAccess, allLocationsData, sessionLocations],
  );

  // Read initial selection from localStorage, validate it, fall back to first.
  const [locationId, setLocationIdState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ?? null;
    } catch {
      return null;
    }
  });

  // Validate/coerce the stored ID whenever the list becomes available.
  useEffect(() => {
    if (locations.length === 0) return;
    const ids = new Set(locations.map((l) => l.id));
    if (!locationId || !ids.has(locationId)) {
      setLocationIdState(locations[0]?.id ?? null);
    }
  }, [locations, locationId]);

  const setLocationId = useCallback((id: string) => {
    setLocationIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Ignore storage errors (private browsing, quota, etc.)
    }
  }, []);

  const location = useMemo(
    () => locations.find((l) => l.id === locationId) ?? null,
    [locations, locationId],
  );

  const value = useMemo<LocationContextValue>(
    () => ({
      location,
      locationId: location?.id ?? null,
      locations,
      isLoadingLocations: isFullAccess && isLoadingLocations,
      setLocationId,
    }),
    [location, locations, isFullAccess, isLoadingLocations, setLocationId],
  );

  return (
    <LocationContext.Provider value={value}>{children}</LocationContext.Provider>
  );
}
